import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { invoiceService, InvoiceRequestError, type Invoice, type InvoiceAvailability, type InvoiceCatalogProduct, type InvoiceCurrency, type InvoiceDeliveryAttempt, type InvoiceDraftInput, type InvoiceLine, type InvoiceTerms, type ManualInvoicePaymentInput } from '../services/invoiceService';
import { CreateInvoiceCatalogDialog } from '../components/CreateInvoiceCatalogDialog';
import { InvoiceCatalogPicker } from '../components/InvoiceCatalogPicker';
import { formatInvoiceDate } from './invoiceDate';
import './invoicePrint.css';

const blankLine = (): InvoiceLine => ({ source_type: 'freeform', description: '', quantity: 1, unit_price: '' });
const blankDraft = (): InvoiceDraftInput => ({
  currency: 'BBD', terms: 'due_on_receipt',
  customer: { name: '', company: '', email: '', billing_address: '' },
  delivery: { method: 'collect', shipping_address: '' },
  discount: { type: 'amount', amount: '0.00' }, lines: [blankLine()],
});
const seller = {
  BBD: { name: 'Made in the Shade Ltd', detail: 'TIN 1000004149348' },
  USD: { name: 'Offshore Sails Ltd', detail: 'A reseller of Made.bb products' },
};
const address = 'Six Crossroads Industrial Estate, Six Crossroads, St Philip, Barbados, BB18056';
const usdFooter = 'Items paid for in USD are produced and sold under license by Offshore Sails Ltd.';
const termsLabel: Record<InvoiceTerms, string> = {
  due_on_receipt: 'Due on receipt', fifty_fifty: '50% deposit / 50% on completion',
  two_ten_net_thirty: '2/10 net 30', net_thirty: 'Net 30',
};

function cents(value: string | number): number {
  const number = Number(value);
  return Number.isFinite(number) ? Math.round(number * 100) : 0;
}
function amount(value: number, currency: InvoiceCurrency) { return `${currency} ${(value / 100).toFixed(2)}`; }
function lineSubtotalCents(line: InvoiceLine): number {
  // Persisted invoice quantities arrive as decimal strings from the API.
  const quantity = Number(line.quantity);
  const quantityMilli = Number.isFinite(quantity) ? Math.round(Math.max(0, quantity) * 1000) : 0;
  const unitCents = cents(line.unit_price);
  if (!Number.isSafeInteger(quantityMilli) || !Number.isSafeInteger(unitCents)) return 0;
  // Match invoiceMoney: round each extended line half up before adding line totals.
  return Number((BigInt(quantityMilli) * BigInt(unitCents) + 500n) / 1000n);
}

export const InvoiceEditorPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [draft, setDraft] = useState<InvoiceDraftInput>(blankDraft);
  const [record, setRecord] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(Boolean(id));
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [catalogPickerOpen, setCatalogPickerOpen] = useState(false);
  const [catalogDialogOpen, setCatalogDialogOpen] = useState(false);
  const [availability, setAvailability] = useState<InvoiceAvailability | null>(null);
  const [issuing, setIssuing] = useState(false);
  const [voiding, setVoiding] = useState(false);
  const [financeAccess, setFinanceAccess] = useState(false);
  const [receiptAmount, setReceiptAmount] = useState('');
  const [receiptReference, setReceiptReference] = useState('');
  const [receiptEvidence, setReceiptEvidence] = useState('');
  const [recordingReceipt, setRecordingReceipt] = useState(false);
  const [manualMethod, setManualMethod] = useState<'cash' | 'card'>('cash');
  const [manualMode, setManualMode] = useState<'amount_due' | 'partial' | 'full'>('amount_due');
  const [manualAmount, setManualAmount] = useState('');
  const [manualReference, setManualReference] = useState('');
  const [manualCashReceived, setManualCashReceived] = useState(false);
  const [recordingManual, setRecordingManual] = useState(false);
  const [manualRetryInput, setManualRetryInput] = useState<ManualInvoicePaymentInput | null>(null);
  const [manualRefreshRequired, setManualRefreshRequired] = useState(false);
  const [openingReceiptId, setOpeningReceiptId] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  const [overrideReasonToConfirm, setOverrideReasonToConfirm] = useState('');
  const [confirmOverrideOpen, setConfirmOverrideOpen] = useState(false);
  const [startingFulfilment, setStartingFulfilment] = useState(false);
  const [fulfilmentRequested, setFulfilmentRequested] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [deliveryAttempts, setDeliveryAttempts] = useState<InvoiceDeliveryAttempt[]>([]);
  const [resolutionAttemptId, setResolutionAttemptId] = useState('');
  const [resolutionOutcome, setResolutionOutcome] = useState<'confirmed_sent' | 'confirmed_not_sent'>('confirmed_sent');
  const [resolutionEvidence, setResolutionEvidence] = useState('');
  const [resolutionProviderId, setResolutionProviderId] = useState('');
  const [resolvingDelivery, setResolvingDelivery] = useState(false);
  const emailRequestKey = useRef<string | null>(null);
  const readOnly = Boolean(record && record.status !== 'draft');
  const hasCatalog = draft.lines.some((line) => line.source_type === 'catalog');

  useEffect(() => {
    if (!id) return;
    let active = true;
    setLoading(true);
    invoiceService.get(id).then((invoice) => {
      if (!active) return;
      setRecord(invoice);
      setFulfilmentRequested(invoice.fulfilment_start_requested === true);
      setDraft({ currency: invoice.currency, terms: invoice.terms, customer: invoice.customer,
        delivery: invoice.delivery || { method: 'collect', shipping_address: '' },
        discount: invoice.discount || { type: 'amount', amount: '0.00' },
        lines: invoice.lines?.length ? invoice.lines : [blankLine()] });
      setDirty(false);
    }).catch((reason) => { if (active) setError(reason instanceof Error ? reason.message : 'Could not load invoice'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [id]);

  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  useEffect(() => {
    if (!record?.id || !['issued', 'part_paid', 'paid'].includes(record.status)
        || (record.currency !== 'USD' && !deliveryAttempts.some((attempt) => attempt.status === 'uncertain'))) {
      setFinanceAccess(false);
      return;
    }
    let active = true;
    invoiceService.financeAccess().then((access) => {
      if (active) setFinanceAccess(access.can_record_usd === true);
    }).catch(() => { if (active) setFinanceAccess(false); });
    return () => { active = false; };
  }, [record?.id, record?.currency, record?.status, deliveryAttempts]);

  const lineSubtotals = useMemo(() => draft.lines.map(lineSubtotalCents), [draft.lines]);
  const preview = useMemo(() => {
    const subtotal = lineSubtotals.reduce((sum, lineSubtotal) => sum + lineSubtotal, 0);
    const discount = Math.min(subtotal, Math.max(0, cents(draft.discount.amount)));
    const taxable = subtotal - discount;
    const vat = draft.currency === 'BBD' ? Math.round(taxable * 0.175) : 0;
    return { subtotal, discount, vat, total: taxable + vat };
  }, [draft.currency, draft.discount.amount, lineSubtotals]);
  const shownAmounts = record?.totals && !dirty ? {
    subtotal: cents(record.totals.subtotal),
    discount: cents(record.totals.invoice_discount),
    vat: cents(record.totals.vat),
    total: cents(record.totals.grand_total),
  } : preview;
  const originalDeposit = draft.terms === 'fifty_fifty'
    ? record?.totals && !dirty && record.totals.deposit != null
      ? cents(record.totals.deposit) : Math.ceil(shownAmounts.total / 2) : null;
  const originalFinal = originalDeposit == null ? null : shownAmounts.total - originalDeposit;
  const currentDue = record?.totals && !dirty
    ? draft.terms === 'fifty_fifty'
      ? Math.max(0, cents(record.totals.paid) < (originalDeposit || 0)
        ? (originalDeposit || 0) - cents(record.totals.paid)
        : record.completed_at ? cents(record.totals.current_balance) : 0)
      : record.early_offer ? cents(record.early_offer.payable) : cents(record.totals.current_balance)
    : draft.terms === 'fifty_fifty' ? originalDeposit : shownAmounts.total;

  const update = (next: InvoiceDraftInput) => { setDraft(next); setDirty(true); setNotice(''); setError(''); };
  const updateCustomer = (field: keyof InvoiceDraftInput['customer'], value: string) => update({ ...draft, customer: { ...draft.customer, [field]: value } });
  const updateLine = (index: number, field: keyof InvoiceLine, value: string | number) => {
    const lines = draft.lines.map((line, i) => i === index ? { ...line, [field]: value } : line);
    if (!lines.some((line) => line.source_type === 'catalog') && draft.terms === 'fifty_fifty') update({ ...draft, lines, terms: 'due_on_receipt' });
    else update({ ...draft, lines });
  };
  const changeCurrency = (currency: InvoiceCurrency) => {
    if (currency === draft.currency) return;
    if (draft.lines.some((line) => line.description || line.unit_price) && !window.confirm(`Switch to ${currency}? Every line needs a price entered in ${currency}; existing prices will be cleared.`)) return;
    update({ ...draft, currency, lines: draft.lines.map((line) => ({ ...line, unit_price: '' })) });
    setNotice(`Enter ${currency} prices for every line before issuing.`);
  };
  const addCatalog = (product: InvoiceCatalogProduct) => {
    const manualBbdPrice = draft.currency === 'BBD' && product.bbd_price === null;
    const line: InvoiceLine = { source_type: 'catalog', catalog_product_id: product.id, sku: product.sku, description: product.name,
      quantity: 1, unit_price: draft.currency === 'BBD' ? product.bbd_price ?? '0.00' : '',
      ...(manualBbdPrice ? { price_source: 'manual_bbd' as const, price_override_reason: '' } : {}),
      fulfilment_type: product.fulfilment_type };
    update({ ...draft, lines: [...draft.lines.filter((existing) => existing.description || existing.unit_price), line] });
    setCatalogPickerOpen(false);
    setNotice(draft.currency === 'USD' ? `Enter the USD unit price for ${product.name} before saving.`
      : manualBbdPrice ? `${product.name} added at BBD 0.00. Enter a one-time unit price and reason before issuing; its Shop Admin Price will stay unchanged.`
        : `${product.name} added at the current Shop Admin public Price in BBD before VAT.`);
  };
  const save = async () => {
    if (saving || readOnly) return;
    setSaving(true); setError(''); setNotice('');
    try {
      const input = { ...draft, lines: draft.lines.filter((line) => line.source_type === 'catalog' || line.description.trim() || line.unit_price) };
      const saved = record ? await invoiceService.update(record.id, record.version, input) : await invoiceService.create(input);
      setRecord(saved);
      setDraft({ currency: saved.currency, terms: saved.terms, customer: saved.customer,
        delivery: saved.delivery || { method: 'collect', shipping_address: '' },
        discount: saved.discount || { type: 'amount', amount: '0.00' },
        lines: saved.lines?.length ? saved.lines : [blankLine()] });
      setDirty(false); setNotice('Draft saved.');
      if (!id) navigate(`/madebb/invoices/${encodeURIComponent(saved.id)}`, { replace: true });
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Save failed. Your entered values are still here.');
    } finally { setSaving(false); }
  };
  const goBack = () => {
    if (dirty && !window.confirm('You have unsaved invoice changes. Leave without saving?')) return;
    navigate(`/madebb/invoices${location.search}`);
  };
  const issue = async (mode?: 'reserve_available' | 'no_inventory_effect') => {
    if (!record || record.status !== 'draft' || dirty || issuing) return;
    setIssuing(true); setError(''); setNotice('');
    try {
      if (!mode) {
        const preview = await invoiceService.availability(record.id, record.version);
        if (preview.has_shortage) {
          setAvailability(preview);
          return;
        }
        if (!window.confirm('Issue this invoice? Its number and financial details will be fixed.')) return;
      }
      const issued = await invoiceService.issue(record.id, record.version, mode, availability?.stock);
      setRecord(issued);
      setAvailability(null);
      setNotice(`Invoice ${issued.number} issued.`);
    } catch (reason) {
      setAvailability(null);
      setError(reason instanceof Error ? reason.message : 'Invoice could not be issued. Review the draft and try again.');
    } finally { setIssuing(false); }
  };
  const voidInvoice = async () => {
    if (!record || record.status !== 'issued' || voiding) return;
    const reason = window.prompt('Reason for voiding this unpaid invoice? The number and audit record will be kept.');
    if (reason == null) return;
    if (reason.trim().length < 3) {
      setError('Enter a reason of at least 3 characters to void this invoice.');
      return;
    }
    setVoiding(true); setError(''); setNotice('');
    try {
      const voided = await invoiceService.void(record.id, record.version, reason.trim());
      setRecord(voided);
      setNotice(`Invoice ${voided.number} voided. Create a new invoice for corrected details.`);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Invoice could not be voided.');
    } finally { setVoiding(false); }
  };
  const recordUsdReceipt = async () => {
    if (!record || !financeAccess || recordingReceipt) return;
    setRecordingReceipt(true); setError(''); setNotice('');
    try {
      await invoiceService.recordOfflineUsdPayment(record.id, {
        source: 'wire', amount: receiptAmount.trim(), reference: receiptReference.trim(),
        evidence_reference: receiptEvidence.trim(),
      });
      setReceiptAmount(''); setReceiptReference(''); setReceiptEvidence('');
      setNotice('Verified USD receipt recorded.');
      try {
        setRecord(await invoiceService.get(record.id));
        setNotice('Verified USD receipt recorded. The invoice balance was refreshed.');
      } catch {
        setError('Receipt was recorded, but the invoice could not be refreshed. Reload it before entering another payment.');
      }
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Receipt could not be recorded.');
    } finally { setRecordingReceipt(false); }
  };
  const recordManualPayment = async () => {
    if (!record?.totals || recordingManual || manualRefreshRequired || !['issued', 'part_paid'].includes(record.status)) return;
    const balance = Number(record.totals.current_balance);
    const due = currentDue || 0;
    const partial = Number(manualAmount);
    if (manualMode === 'amount_due' && due <= 0) { setError('No instalment is due yet. Choose Pay in Full to record an early final payment.'); return; }
    if (manualMode === 'partial' && (!/^\d+(?:\.\d{1,2})?$/.test(manualAmount.trim()) || !Number.isFinite(partial) || partial <= 0 || Math.round(partial * 100) >= Math.round(balance * 100))) {
      setError('Enter a partial amount greater than zero and less than the remaining balance.'); return;
    }
    if (!Number.isFinite(balance) || balance <= 0) { setError('This invoice has no balance to pay.'); return; }
    if (manualMethod === 'card' && !manualReference.trim()) { setError('Enter the card terminal transaction reference.'); return; }
    if (manualMethod === 'cash' && !manualCashReceived) { setError('Confirm that the cash has been received.'); return; }
    if (!manualRetryInput && !globalThis.crypto?.randomUUID) { setError('Secure payment request IDs are unavailable in this browser.'); return; }
    const quotedCents = manualMode === 'partial' ? cents(manualAmount)
      : manualMode === 'full' ? cents(record.early_offer?.payable || record.totals.current_balance) : currentDue;
    const input: ManualInvoicePaymentInput = manualRetryInput || {
      method: manualMethod, mode: manualMode,
      ...(manualMode === 'partial' ? { amount: manualAmount.trim() } : {}),
      ...(manualMethod === 'card' ? { reference: manualReference.trim() } : { cash_received: true }),
      request_key: globalThis.crypto.randomUUID(), expected_balance: record.totals.current_balance,
      expected_payment_amount: (quotedCents / 100).toFixed(2),
    };
    setManualRetryInput(input);
    setRecordingManual(true); setError(''); setNotice('');
    try {
      const result = await invoiceService.recordManualPayment(record.id, input);
      setManualRetryInput(null);
      setManualAmount(''); setManualReference(''); setManualCashReceived(false);
      setNotice(`${result.currency} ${result.amount} recorded. Receipt ${result.receipt_number} is available in payment history.`);
      try { setRecord(await invoiceService.get(record.id)); }
      catch { setManualRefreshRequired(true); setError('Payment was recorded, but the invoice could not be refreshed. Refresh the invoice before entering another payment.'); }
    } catch (reason) {
      if (reason instanceof InvoiceRequestError && reason.status >= 400 && reason.status < 500) {
        setManualRetryInput(null);
        if (reason.status === 409) setManualRefreshRequired(true);
        setError(`${reason.message}${reason.status === 409 ? ' Refresh the invoice and payment history before another attempt.' : ''}`);
      } else {
        setError(`${reason instanceof Error ? reason.message : 'Payment outcome is unknown.'} Retry the same request or refresh the invoice and check payment history before entering a different payment.`);
      }
    } finally { setRecordingManual(false); }
  };
  const refreshInvoice = async () => {
    if (!record) return;
    setError('');
    try {
      const refreshed = await invoiceService.get(record.id);
      setRecord(refreshed);
      setFulfilmentRequested(refreshed.fulfilment_start_requested === true);
      setManualRetryInput(null);
      setManualRefreshRequired(false);
      setNotice('Invoice and payment history refreshed.');
    } catch (reason) { setError(reason instanceof Error ? reason.message : 'Invoice could not be refreshed.'); }
  };
  const openReceipt = async (paymentId: string) => {
    if (!record || openingReceiptId) return;
    const printWindow = window.open('about:blank', '_blank');
    if (!printWindow) { setError('Allow popups to open the printable receipt.'); return; }
    printWindow.opener = null;
    setOpeningReceiptId(paymentId); setError('');
    try {
      const html = await invoiceService.receiptHtml(record.id, paymentId);
      const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
      printWindow.location.href = url;
      window.setTimeout(() => URL.revokeObjectURL(url), 120_000);
    } catch (reason) {
      printWindow.close();
      setError(reason instanceof Error ? reason.message : 'Receipt could not be opened.');
    } finally { setOpeningReceiptId(''); }
  };
  const startFulfilmentEarly = async () => {
    if (!record || startingFulfilment || fulfilmentRequested || overrideReasonToConfirm.trim().length < 3) return;
    setConfirmOverrideOpen(false);
    setStartingFulfilment(true); setError(''); setNotice('');
    try {
      const result = await invoiceService.startFulfilment(record.id, overrideReasonToConfirm.trim());
      setFulfilmentRequested(true);
      setOverrideReason('');
      setOverrideReasonToConfirm('');
      setNotice(`Fulfilment start requested${result.handoff_status === 'pending_retry' ? ' and queued for retry' : ''}. Payment remains due.`);
      try { setRecord(await invoiceService.get(record.id)); }
      catch { setError('Fulfilment was requested, but the invoice could not be refreshed. Reload before retrying.'); }
    } catch (reason) { setError(reason instanceof Error ? reason.message : 'Fulfilment could not be started.'); }
    finally { setStartingFulfilment(false); }
  };
  const refreshDelivery = async () => {
    if (!record) return;
    try {
      const attempts = await invoiceService.deliveryAttempts(record.id);
      setDeliveryAttempts(attempts);
      const matched = attempts.find((attempt) => attempt.request_key === emailRequestKey.current);
      if (matched?.status === 'sent' || matched?.status === 'failed') emailRequestKey.current = null;
      setRecord(await invoiceService.get(record.id));
      setNotice('Delivery history refreshed.');
    } catch (reason) { setError(reason instanceof Error ? reason.message : 'Delivery history could not be loaded.'); }
  };
  const sendEmail = async () => {
    if (!record || sendingEmail) return;
    if (!globalThis.crypto?.randomUUID) {
      setError('Secure email request IDs are unavailable in this browser.');
      return;
    }
    emailRequestKey.current ||= globalThis.crypto.randomUUID();
    setSendingEmail(true); setError(''); setNotice('');
    try {
      const attempt = await invoiceService.sendEmail(record.id, emailRequestKey.current);
      setDeliveryAttempts((current) => [attempt, ...current.filter((item) => item.id !== attempt.id)]);
      if (attempt.status === 'sent') {
        emailRequestKey.current = null;
        setNotice(`Invoice email sent to ${attempt.recipient_email}.`);
        try { setRecord(await invoiceService.get(record.id)); }
        catch { setError('Email was sent, but the invoice status could not be refreshed. Reload to see its latest state.'); }
      } else if (attempt.status === 'failed') {
        emailRequestKey.current = null;
        setError('Invoice email failed. Review delivery history before retrying.');
      } else if (attempt.status === 'uncertain') {
        setError('Email delivery is uncertain. Finance staff must check the provider and record the outcome before another send.');
      } else setNotice('Invoice email is being sent. Refresh delivery history before retrying.');
    } catch (reason) {
      setError(`${reason instanceof Error ? reason.message : 'Email status is unknown.'} Check delivery history before retrying.`);
    } finally { setSendingEmail(false); }
  };
  const resolveDelivery = async () => {
    if (!record || !financeAccess || !resolutionAttemptId || resolvingDelivery) return;
    setResolvingDelivery(true); setError(''); setNotice('');
    try {
      const resolved = await invoiceService.resolveDeliveryAttempt(record.id, resolutionAttemptId, {
        outcome: resolutionOutcome, evidence_reference: resolutionEvidence.trim(),
        provider_message_id: resolutionOutcome === 'confirmed_sent' ? resolutionProviderId.trim() : undefined,
      });
      setDeliveryAttempts((current) => current.map((item) => item.id === resolved.id ? resolved : item));
      setResolutionAttemptId(''); setResolutionEvidence(''); setResolutionProviderId('');
      emailRequestKey.current = null;
      setRecord(await invoiceService.get(record.id));
      setNotice(resolutionOutcome === 'confirmed_sent'
        ? 'Provider delivery confirmed and recorded.' : 'Provider non-delivery confirmed. A new email can now be sent.');
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Delivery outcome could not be recorded.');
    } finally { setResolvingDelivery(false); }
  };

  if (loading) return <Stack direction="row" spacing={1} alignItems="center" role="status"><CircularProgress size={20} /><span>Loading invoice…</span></Stack>;
  if (id && !record) return <Alert severity="error" action={<Button onClick={goBack}>Back to invoices</Button>}>{error || 'Invoice not found'}</Alert>;

  const label = record?.number || 'DRAFT — not issued';
  const lineRows = draft.lines;
  const shownSeller = readOnly && record?.seller ? record.seller : {
    name: seller[draft.currency].name,
    descriptor: draft.currency === 'USD' ? seller.USD.detail : undefined,
    address,
    email: 'info@made.bb',
    tin: draft.currency === 'BBD' ? '1000004149348' : undefined,
    footer: draft.currency === 'USD' ? usdFooter : undefined,
  };
  const manualEntryLocked = Boolean(manualRetryInput) || manualRefreshRequired;
  return <Box data-testid="invoice-editor">
    <Stack className="invoice-actions" direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between" mb={2}>
      <Button onClick={goBack}>← Invoices</Button>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography role="status" color={dirty ? 'warning.main' : 'text.secondary'}>{saving ? 'Saving…' : dirty ? 'Unsaved changes' : record ? 'Saved' : 'Not saved'}</Typography>
        {!readOnly && <Button variant="outlined" disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save draft'}</Button>}
        {record?.status === 'draft' && <Button variant="contained" disabled={saving || issuing || dirty} onClick={() => issue()}>{issuing ? 'Checking stock…' : 'Issue invoice'}</Button>}
        {record?.status === 'issued' && <Button color="error" variant="outlined" disabled={voiding}
          onClick={voidInvoice}>{voiding ? 'Voiding…' : 'Void invoice'}</Button>}
        <Button variant="outlined" onClick={() => window.print()}>Print / PDF</Button>
      </Stack>
    </Stack>
    {error && <Alert severity="error" role="alert" className="invoice-actions" sx={{ mb: 2 }}>{error} {error.toLowerCase().includes('version') && 'Reload this invoice after copying your changes.'}</Alert>}
    {notice && <Alert severity="success" className="invoice-actions" sx={{ mb: 2 }}>{notice}</Alert>}
    {availability?.has_shortage && <Alert severity="warning" className="invoice-actions" sx={{ mb: 2 }}>
      <Typography fontWeight={700}>Some Buy items do not have enough available stock.</Typography>
      {availability.stock.filter((line) => line.shortfall_milli > 0).map((line) => <Typography key={line.invoice_line_id} variant="body2">
        {line.description}: {line.shortfall_milli / 1000} awaiting stock; {line.reservable_milli / 1000} can be reserved now.
      </Typography>)}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} mt={1}>
        <Button disabled={issuing} onClick={() => issue('reserve_available')}>Reserve available stock; remainder awaiting stock</Button>
        <Button disabled={issuing} onClick={() => issue('no_inventory_effect')}>Issue with no inventory effect</Button>
        <Button disabled={issuing} onClick={() => setAvailability(null)}>Cancel</Button>
      </Stack>
    </Alert>}
    {readOnly && <Alert severity={record?.status === 'voided' ? 'warning' : 'info'} className="invoice-actions" sx={{ mb: 2 }}>
      {record?.status === 'voided' ? `VOIDED. ${record.void_reason || 'This invoice is an audit record.'}`
        : 'Issued financial details are read-only. Corrections require voiding and replacing an eligible unpaid invoice.'}
    </Alert>}
    <Paper className="invoice-sheet" variant="outlined" sx={{ p: { xs: 2, md: 5 }, maxWidth: 900, mx: 'auto', bgcolor: 'white' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={3}>
        <Box>
          <img src="/madebb/logo.png" alt="Made.bb" className="invoice-logo" />
          <Typography variant="h6" fontWeight={700}>{shownSeller.name}</Typography>
          {shownSeller.descriptor && <Typography variant="body2">{shownSeller.descriptor}</Typography>}
          {shownSeller.tin && <Typography variant="body2">TIN {shownSeller.tin}</Typography>}
          <Typography variant="body2">{shownSeller.address || address}</Typography>
          <Typography variant="body2">{shownSeller.email || 'info@made.bb'}</Typography>
        </Box>
        <Box sx={{ minWidth: 220 }}>
          <Typography variant="h4" fontWeight={700}>{record?.status === 'voided' ? 'VOIDED INVOICE' : 'INVOICE'}</Typography>
          <Typography><strong>{label}</strong></Typography>
          <Typography variant="body2">Issue date: {record?.issue_date ? formatInvoiceDate(record.issue_date) : 'Assigned when issued'}</Typography>
          <Typography variant="body2">Due date: {record?.due_date ? formatInvoiceDate(record.due_date, true) : 'Calculated when issued'}</Typography>
          <Typography variant="body2">Salesperson: {record?.salesperson_name || user?.displayName || user?.email || 'Signed-in staff'}</Typography>
        </Box>
      </Stack>
      <Divider sx={{ my: 3 }} />
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
        <Box flex={1}>
          <Typography variant="subtitle2" mb={1}>Bill to</Typography>
          <Stack spacing={1} className="invoice-customer-fields">
            <TextField label="Customer name" value={draft.customer.name} onChange={(event) => updateCustomer('name', event.target.value)} disabled={readOnly} required size="small" fullWidth />
            <TextField className={draft.customer.company ? 'invoice-company' : 'invoice-company invoice-company-empty'} label="Company" value={draft.customer.company || ''} onChange={(event) => updateCustomer('company', event.target.value)} disabled={readOnly} size="small" fullWidth />
            <TextField label="Billing address" value={draft.customer.billing_address} onChange={(event) => updateCustomer('billing_address', event.target.value)} disabled={readOnly} required multiline minRows={2} size="small" fullWidth />
            <TextField label="Customer email" type="email" value={draft.customer.email} onChange={(event) => updateCustomer('email', event.target.value)} disabled={readOnly} required size="small" fullWidth />
          </Stack>
        </Box>
        <Stack spacing={1} sx={{ width: { md: 240 } }}>
          <TextField select label="Currency" value={draft.currency} onChange={(event) => changeCurrency(event.target.value as InvoiceCurrency)} disabled={readOnly} size="small"><MenuItem value="BBD">BBD</MenuItem><MenuItem value="USD">USD</MenuItem></TextField>
          <TextField select label="Terms" value={draft.terms} onChange={(event) => update({ ...draft, terms: event.target.value as InvoiceTerms })} disabled={readOnly} size="small">
            {(Object.keys(termsLabel) as InvoiceTerms[]).map((term) => <MenuItem key={term} value={term} disabled={term === 'fifty_fifty' && !hasCatalog}>{termsLabel[term]}</MenuItem>)}
          </TextField>
          {draft.terms === 'fifty_fifty' && <Typography variant="caption">Deposit due now; balance due when linked work is complete.</Typography>}
          <TextField select label="Delivery method" value={draft.delivery.method}
            onChange={(event) => update({ ...draft, delivery: { ...draft.delivery, method: event.target.value as 'ship' | 'collect' } })}
            disabled={readOnly} size="small">
            <MenuItem value="collect">Ready for collection</MenuItem>
            <MenuItem value="ship">Ship to customer</MenuItem>
          </TextField>
          {draft.delivery.method === 'ship' && <TextField label="Shipping address"
            value={draft.delivery.shipping_address}
            onChange={(event) => update({ ...draft, delivery: { ...draft.delivery, shipping_address: event.target.value } })}
            disabled={readOnly} required multiline minRows={2} size="small" />}
        </Stack>
      </Stack>
      <Box mt={4} className="invoice-lines">
        <table className="invoice-line-table">
          <thead><tr><th scope="col">Description</th><th scope="col">Qty</th><th scope="col">Unit price</th><th scope="col">Line total</th><th scope="col" className="invoice-actions">Actions</th></tr></thead>
          <tbody>{lineRows.map((line, index) => <tr className="invoice-line" key={line.id || index}>
            <td><TextField className={line.source_type === 'freeform' ? 'invoice-line-freeform' : 'invoice-line-catalog'} label={`Line ${index + 1} description`} value={line.description} onChange={(event) => updateLine(index, 'description', event.target.value)} disabled={readOnly || line.source_type === 'catalog'} size="small" fullWidth multiline minRows={1} /></td>
            <td><TextField label="Quantity" type="number" inputProps={{ min: 0.001, step: 0.001 }} value={line.quantity} onChange={(event) => updateLine(index, 'quantity', Number(event.target.value))} disabled={readOnly} size="small" fullWidth /></td>
            <td>
              <TextField label={`Unit price ${draft.currency}`} type="number" inputProps={{ min: 0, step: 0.01 }} value={line.unit_price} onChange={(event) => updateLine(index, 'unit_price', event.target.value)} disabled={readOnly || (line.source_type === 'catalog' && draft.currency === 'BBD' && line.price_source !== 'manual_bbd')} size="small" fullWidth />
              {draft.currency === 'BBD' && line.price_source === 'manual_bbd' && !readOnly && <TextField className="invoice-actions" label="One-time price reason" value={line.price_override_reason || ''}
                onChange={(event) => updateLine(index, 'price_override_reason', event.target.value)} required size="small" fullWidth multiline minRows={2}
                helperText="Internal note required before issuing; the Shop Admin Price is unchanged." sx={{ mt: 1 }} />}
            </td>
            <td>{amount(lineSubtotals[index], draft.currency)}</td>
            <td className="invoice-actions">{!readOnly && <Button size="small" onClick={() => update({ ...draft, lines: draft.lines.filter((_, i) => i !== index) })} aria-label={`Remove line ${index + 1}`}>Remove</Button>}</td>
          </tr>)}</tbody>
        </table>
        {!readOnly && <Box className="invoice-actions" mt={2}>
          <Button onClick={() => update({ ...draft, lines: [...draft.lines, blankLine()] })}>Add one-off line</Button>
          <Button onClick={() => setCatalogDialogOpen(true)}>Create inventory product</Button>
          <Button onClick={() => setCatalogPickerOpen(true)}>Find product</Button>
        </Box>}
      </Box>
      <Divider sx={{ my: 3 }} />
      <Stack spacing={1} sx={{ maxWidth: 360, ml: 'auto' }} className="invoice-totals">
        <Stack direction="row" justifyContent="space-between"><span>Subtotal</span><strong>{amount(shownAmounts.subtotal, draft.currency)}</strong></Stack>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}><Box className="invoice-discount-edit"><TextField label={`Invoice discount ${draft.currency}`} type="number" inputProps={{ min: 0, step: 0.01 }} value={draft.discount.amount} onChange={(event) => update({ ...draft, discount: { type: 'amount', amount: event.target.value } })} disabled={readOnly} size="small" /></Box><span className="invoice-discount-print">Invoice discount</span><strong>− {amount(shownAmounts.discount, draft.currency)}</strong></Stack>
        <Stack direction="row" justifyContent="space-between"><span>VAT {draft.currency === 'BBD' ? '(17.5%)' : '(0%)'}</span><strong>{amount(shownAmounts.vat, draft.currency)}</strong></Stack>
        <Divider />
        <Stack direction="row" justifyContent="space-between"><Typography variant="h6">Total</Typography><Typography variant="h6" fontWeight={700}>{amount(shownAmounts.total, draft.currency)}</Typography></Stack>
        {originalDeposit != null && <Stack direction="row" justifyContent="space-between"><span>Deposit due on issue</span><strong>{amount(originalDeposit, draft.currency)}</strong></Stack>}
        {originalFinal != null && <Stack direction="row" justifyContent="space-between"><span>Final instalment due on completion</span><strong>{amount(originalFinal, draft.currency)}</strong></Stack>}
        {record?.adjustments?.map((adjustment, index) => <React.Fragment key={`${adjustment.kind}-${index}`}>
          <Stack direction="row" justifyContent="space-between" spacing={2}><span>{adjustment.kind === 'early_payment_discount' ? 'Earned early payment discount' : adjustment.reason} ({adjustment.date})</span><strong>{draft.currency} {adjustment.base_delta}</strong></Stack>
          {draft.currency === 'BBD' && <Stack direction="row" justifyContent="space-between"><span>VAT adjustment</span><strong>{draft.currency} {adjustment.vat_delta}</strong></Stack>}
        </React.Fragment>)}
        {record?.adjustments?.length && draft.currency === 'BBD' ? <Stack direction="row" justifyContent="space-between"><span>Revised VAT</span><strong>{amount(shownAmounts.vat + record.adjustments.reduce((sum, adjustment) => sum + cents(adjustment.vat_delta), 0), draft.currency)}</strong></Stack> : null}
        {record?.totals && !dirty && <Typography variant="caption">Balance: {draft.currency} {record.totals.current_balance}</Typography>}
        <Stack direction="row" justifyContent="space-between"><span>{record?.early_offer ? 'Amount due now with early payment offer' : 'Amount due now'}</span><strong>{amount(currentDue || 0, draft.currency)}</strong></Stack>
        {record?.early_offer && <Typography variant="caption">2% early payment offer: pay in full by {record.early_offer.deadline}. If verified in time, the invoice will record a {draft.currency} {record.early_offer.base_discount} base discount and VAT of {draft.currency} {record.early_offer.revised_vat}.</Typography>}
        {(!record?.totals || dirty) && <Typography variant="caption">Draft estimate. Final amounts are calculated and saved by the server.</Typography>}
      </Stack>
      {draft.currency === 'USD' && <Typography className="invoice-footer" variant="body2" mt={5}>{shownSeller.footer || usdFooter}</Typography>}
      {draft.currency === 'BBD' && <Typography className="invoice-footer" variant="body2" mt={5}>Thank you for choosing Made.bb.</Typography>}
    </Paper>
    {readOnly && <Paper className="invoice-actions" variant="outlined" sx={{ maxWidth: 900, mx: 'auto', mt: 2, p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="h6">Payment history</Typography>
        <Button size="small" onClick={refreshInvoice}>Refresh invoice</Button>
      </Stack>
      {!record?.payments?.length ? <Typography variant="body2">No verified payments recorded.</Typography>
        : record.payments.map((payment) => <Stack key={payment.id} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} mt={1}>
          <Typography variant="body2">
            {payment.verified_at?.slice(0, 10)} · {payment.source.replaceAll('_', ' ')}
            {payment.installment ? ` · ${payment.installment}` : ''} · {payment.currency} {payment.amount}
            {payment.reference ? ` · Ref ${payment.reference}` : ''}
            {payment.verified_by_name || payment.verified_by ? ` · Recorded by ${payment.verified_by_name || payment.verified_by}` : ''}
            {payment.receipt_number ? ` · ${payment.receipt_number}` : ''}
          </Typography>
          {payment.receipt_number && <Button size="small" disabled={openingReceiptId === payment.id}
            onClick={() => openReceipt(payment.id)} aria-label={`Print receipt ${payment.receipt_number}`}>
            {openingReceiptId === payment.id ? 'Opening…' : 'Print receipt'}
          </Button>}
        </Stack>)}
      {record && ['issued', 'part_paid'].includes(record.status) && record.totals && Number(record.totals.current_balance) > 0 && <Box mt={3}>
        <Divider sx={{ mb: 2 }} />
        <Typography variant="subtitle1" fontWeight={700}>Record manual payment</Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Record cash already received or a card payment already taken on your terminal. This form does not charge a card.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} mb={1}>
          <TextField select label="Manual payment method" size="small" value={manualMethod} disabled={manualEntryLocked}
            onChange={(event) => { setManualMethod(event.target.value as 'cash' | 'card'); setManualReference(''); setManualCashReceived(false); }} sx={{ minWidth: 180 }}>
            <MenuItem value="cash">Cash received</MenuItem><MenuItem value="card">Card terminal payment</MenuItem>
          </TextField>
          <TextField select label="Payment amount option" size="small" value={manualMode} disabled={manualEntryLocked}
            onChange={(event) => setManualMode(event.target.value as 'amount_due' | 'partial' | 'full')} sx={{ minWidth: 200 }}>
            <MenuItem value="amount_due" disabled={currentDue <= 0}>Pay amount due</MenuItem>
            <MenuItem value="partial" disabled={cents(record.totals.current_balance) <= 1}>Make partial payment</MenuItem>
            <MenuItem value="full">Pay in full</MenuItem>
          </TextField>
          {manualMode === 'partial' ? <TextField label={`Partial payment amount ${record.currency}`} type="number" size="small"
            inputProps={{ min: 0.01, max: Math.max(0, (cents(record.totals.current_balance) - 1) / 100).toFixed(2), step: 0.01 }}
            value={manualAmount} disabled={manualEntryLocked} onChange={(event) => setManualAmount(event.target.value)} required />
            : <TextField label={`Payment amount ${record.currency}`} size="small" value={manualMode === 'full' ? (record.early_offer?.payable || record.totals.current_balance) : (currentDue / 100).toFixed(2)} disabled />}
        </Stack>
        {record.early_offer && manualMode !== 'partial' && <Typography variant="body2" color="text.secondary" mb={1}>
          The shown amount includes the 2% early payment discount and revised VAT. The adjustment is recorded when this payment succeeds.
        </Typography>}
        {manualMethod === 'card' ? <TextField label="Card terminal transaction reference" size="small" value={manualReference}
          disabled={manualEntryLocked} onChange={(event) => setManualReference(event.target.value)} required fullWidth sx={{ maxWidth: 420 }} />
          : <FormControlLabel control={<Checkbox checked={manualCashReceived} disabled={manualEntryLocked}
            onChange={(event) => setManualCashReceived(event.target.checked)} />} label="I confirm the cash has been received" />}
        {record.terms === 'fifty_fifty' && <Typography variant="body2" color="text.secondary" mt={1}>
          Pay in Full may settle the final half before delivery or completion. Work starts once the deposit threshold is met.
        </Typography>}
        <Box mt={1}><Button variant="contained" disabled={recordingManual || manualRefreshRequired || (!manualRetryInput && (
          (manualMode === 'amount_due' && currentDue <= 0) ||
          (manualMode === 'partial' && (!manualAmount || cents(manualAmount) <= 0 || cents(manualAmount) >= cents(record.totals.current_balance))) ||
          (manualMethod === 'card' && !manualReference.trim()) || (manualMethod === 'cash' && !manualCashReceived)
        ))} onClick={recordManualPayment}>
          {recordingManual ? 'Recording…' : manualRetryInput ? 'Retry same payment' : 'Record payment and create receipt'}
        </Button></Box>
      </Box>}
    </Paper>}
    {readOnly && record && ['issued', 'part_paid', 'paid'].includes(record.status) && record.lines.some((line) => line.source_type === 'catalog') && <Paper className="invoice-actions" variant="outlined" sx={{ maxWidth: 900, mx: 'auto', mt: 2, p: 2 }}>
      <Typography variant="h6" mb={1}>Production and fulfilment</Typography>
      {record.fulfillment_override && <Typography variant="body2" mb={1}>
        Started early by {record.fulfillment_override.actor_name || record.fulfillment_override.actor_id} on {record.fulfillment_override.occurred_at.slice(0, 10)} · Reason: {record.fulfillment_override.reason}
      </Typography>}
      {fulfilmentRequested || record.fulfilment_start_requested ? <Alert severity={record.fulfilment_status === 'handoff_failed' ? 'warning' : 'info'}>
        Fulfilment has been requested. Later payments will not start it again.
        {record.fulfilment_status === 'handoff_failed' && ' The handoff is pending retry; staff should check the manufacturing queue.'}
      </Alert>
        : record.status === 'paid' ? <Alert severity="info">Payment is complete. Fulfilment will start through the normal workflow.</Alert>
        : <Stack spacing={1}>
          <Typography variant="body2">Start work before the required payment. The invoice balance remains due, and the reason is kept in the audit history. A completion email waits until the required payment is recorded.</Typography>
          <TextField label="Reason for starting before payment" value={overrideReason} onChange={(event) => setOverrideReason(event.target.value)}
            size="small" required multiline minRows={2} fullWidth disabled={startingFulfilment} />
          <Box><Button variant="outlined" disabled={startingFulfilment || overrideReason.trim().length < 3} onClick={() => {
            setOverrideReasonToConfirm(overrideReason.trim()); setConfirmOverrideOpen(true);
          }}>
            {startingFulfilment ? 'Starting…' : 'Start fulfilment before payment'}
          </Button></Box>
        </Stack>}
    </Paper>}
    <Dialog open={confirmOverrideOpen} onClose={() => setConfirmOverrideOpen(false)} aria-labelledby="confirm-invoice-fulfilment-title">
      <DialogTitle id="confirm-invoice-fulfilment-title">Start fulfilment before payment?</DialogTitle>
      <DialogContent>
        <Typography>Invoice {record?.number} · {record?.customer.name}</Typography>
        <Typography>Unpaid balance: {record?.currency} {record?.totals?.current_balance || '0.00'}</Typography>
        <Typography sx={{ mt: 1 }}>Reason: {overrideReasonToConfirm}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>This starts catalog work now and records your reason. The customer completion email waits for the required payment.</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setConfirmOverrideOpen(false)}>Cancel</Button>
        <Button variant="contained" onClick={startFulfilmentEarly}>Confirm start</Button>
      </DialogActions>
    </Dialog>
    {readOnly && ['issued', 'part_paid', 'paid'].includes(record!.status) && <Paper className="invoice-actions" variant="outlined" sx={{ maxWidth: 900, mx: 'auto', mt: 2, p: 2 }}>
      <Typography variant="h6" mb={1}>Customer email</Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>The customer receives a secure printable invoice link. BBD invoices include the card payment link when available.</Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <Button variant="contained" disabled={sendingEmail || deliveryAttempts.some((attempt) => attempt.status === 'uncertain')}
          onClick={sendEmail}>{sendingEmail ? 'Sending…' : emailRequestKey.current ? 'Retry same send' : 'Send invoice email'}</Button>
        <Button disabled={sendingEmail} onClick={refreshDelivery}>Refresh delivery history</Button>
      </Stack>
      {deliveryAttempts.map((attempt) => <Box key={attempt.id} mt={1}>
        <Typography variant="body2">{attempt.status} · {attempt.created_at} · {attempt.recipient_email}{attempt.error_code ? ` · ${attempt.error_code}` : ''}{attempt.resolution_reference ? ` · Evidence ${attempt.resolution_reference}` : ''}</Typography>
        {attempt.status === 'uncertain' && <Alert severity="warning" sx={{ mt: 1 }}>
          Check the email provider before another send. The result could be delivered even without a confirmation here.
          {financeAccess && <Button size="small" onClick={() => setResolutionAttemptId(attempt.id)}>Record provider outcome</Button>}
        </Alert>}
      </Box>)}
      {financeAccess && resolutionAttemptId && <Stack spacing={1} mt={2}>
        <Typography variant="subtitle2">Resolve uncertain delivery</Typography>
        <TextField select label="Provider outcome" value={resolutionOutcome} size="small"
          onChange={(event) => setResolutionOutcome(event.target.value as 'confirmed_sent' | 'confirmed_not_sent')}>
          <MenuItem value="confirmed_sent">Confirmed sent</MenuItem>
          <MenuItem value="confirmed_not_sent">Confirmed not sent</MenuItem>
        </TextField>
        <TextField label="Provider evidence reference" value={resolutionEvidence} size="small" required
          onChange={(event) => setResolutionEvidence(event.target.value)} />
        {resolutionOutcome === 'confirmed_sent' && <TextField label="Provider message ID" value={resolutionProviderId} size="small" required
          onChange={(event) => setResolutionProviderId(event.target.value)} />}
        <Stack direction="row" spacing={1}>
          <Button variant="contained" disabled={resolvingDelivery || !resolutionEvidence.trim() || (resolutionOutcome === 'confirmed_sent' && !resolutionProviderId.trim())}
            onClick={resolveDelivery}>{resolvingDelivery ? 'Recording…' : 'Record outcome'}</Button>
          <Button onClick={() => setResolutionAttemptId('')}>Cancel</Button>
        </Stack>
      </Stack>}
    </Paper>}
    {financeAccess && record?.currency === 'USD' && ['issued', 'part_paid'].includes(record.status) && <Paper className="invoice-actions" variant="outlined" sx={{ maxWidth: 900, mx: 'auto', mt: 2, p: 2 }}>
      <Typography variant="h6" mb={1}>Record verified USD wire</Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>Finance staff only. Enter the bank reference and evidence. For terminal card payments, use Record manual payment above.</Typography>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
        <TextField label="Amount received USD" type="number" inputProps={{ min: 0.01, step: 0.01 }} value={receiptAmount} onChange={(event) => setReceiptAmount(event.target.value)} size="small" required />
        <TextField label="Bank reference" value={receiptReference} onChange={(event) => setReceiptReference(event.target.value)} size="small" required />
        <TextField label="Evidence reference" value={receiptEvidence} onChange={(event) => setReceiptEvidence(event.target.value)} size="small" required />
      </Stack>
      <Button sx={{ mt: 2 }} variant="contained" disabled={recordingReceipt || !receiptAmount || !receiptReference.trim() || !receiptEvidence.trim()} onClick={recordUsdReceipt}>{recordingReceipt ? 'Recording…' : 'Record verified receipt'}</Button>
    </Paper>}
    {!readOnly && !record && <Alert severity="info" className="invoice-actions" sx={{ maxWidth: 900, mx: 'auto', mt: 2 }}>Save this invoice as a draft before issuing it.</Alert>}
    <InvoiceCatalogPicker open={catalogPickerOpen} currency={draft.currency} onClose={() => setCatalogPickerOpen(false)} onSelect={addCatalog} />
    <CreateInvoiceCatalogDialog open={catalogDialogOpen} onClose={() => setCatalogDialogOpen(false)} onCreated={(product) => { addCatalog(product); setNotice(`${product.name} was created hidden from the public shop and added to this invoice. ${draft.currency === 'USD' ? 'Enter its USD invoice price before saving.' : 'Its BBD retail price before VAT was used.'}`); }} />
  </Box>;
};
